import type { SideNavPreferences } from '@/services/db/user-settings/entity';
import { isNavItemActive, type NavItem, type NavItemGroup } from './routes';

export type { SideNavPreferences } from '@/services/db/user-settings/entity';
export type SideNavVisibility = 'show' | 'hide';

export const SIDE_NAV_VISIBILITY_OPTIONS = [
  { label: 'Show', value: 'show' },
  { label: 'Hide', value: 'hide' },
] as const;

export interface CustomizableNavItem extends NavItem {
  defaultVisibility: SideNavVisibility;
  effectiveVisibility: SideNavVisibility;
}

export interface CustomizableNavItemGroup {
  title: string;
  items: CustomizableNavItem[];
}

export interface HiddenNavItem {
  item: NavItem;
  section: string | null;
  parentItem: string | null;
}

type PresentationNavItemVisibilityProps = {
  isVisibleWhenDeselected: NonNullable<NavItem['isVisibleWhenDeselected']>;
  isVisibleWhenSelected: NonNullable<NavItem['isVisibleWhenSelected']>;
};

export type PresentationNavItem = Omit<NavItem, keyof PresentationNavItemVisibilityProps> &
  PresentationNavItemVisibilityProps;

export interface PresentationNavItemGroup extends Omit<NavItemGroup, 'items'> {
  isVisibleInSection: boolean;
  items: PresentationNavItem[];
}

type SideNavEnvironmentPreferences = NonNullable<NonNullable<SideNavPreferences['environments']>[string]>;
type SideNavItemPreferenceMap = NonNullable<SideNavPreferences['items']>;
type SideNavPreferencesWithPassthrough = SideNavPreferences & Record<string, unknown>;
type SideNavEnvironmentPreferencesWithPassthrough = SideNavEnvironmentPreferences & Record<string, unknown>;

const objectTypeNavItemIdPrefix = 'object-types_';

export function getSideNavPreferenceData(
  navItemGroups: NavItemGroup[],
  preferences: SideNavPreferences | undefined,
  environmentId?: string,
) {
  const customizationGroups = getCustomizableNavItemGroups(navItemGroups, preferences, environmentId);

  return {
    customizationGroups,
    hiddenItems: getHiddenNavItems(navItemGroups, preferences, environmentId),
    permanentMoreItems: getPermanentMoreItems(navItemGroups),
    permanentMorePresentationItems: getPermanentMorePresentationItems(navItemGroups),
  };
}

export function buildSideNavPreferences(
  currentPreferences: SideNavPreferences | undefined,
  customizationGroups: CustomizableNavItemGroup[],
  selections: Record<string, SideNavVisibility>,
  environmentId?: string,
): SideNavPreferences {
  const nextPreferences = cloneSideNavPreferences(currentPreferences);
  const nextItems: SideNavItemPreferenceMap = { ...(nextPreferences.items ?? {}) };
  const nextEnvironments: Record<string, SideNavEnvironmentPreferencesWithPassthrough> = {
    ...(nextPreferences.environments ?? {}),
  };

  for (const item of customizationGroups.flatMap((group) => group.items)) {
    const visibility = selections[item.id] ?? item.effectiveVisibility;
    const objectTypeId = getObjectTypePreferenceId(item.id);

    if (objectTypeId != null && environmentId != null) {
      delete nextItems[item.id];
      applyObjectTypeVisibilityPreference(nextEnvironments, environmentId, objectTypeId, item, visibility);
      continue;
    }

    applyItemVisibilityPreference(nextItems, item, visibility);
  }

  if (Object.keys(nextItems).length === 0) {
    delete nextPreferences.items;
  } else {
    nextPreferences.items = nextItems;
  }

  if (Object.keys(nextEnvironments).length === 0) {
    delete nextPreferences.environments;
  } else {
    nextPreferences.environments = nextEnvironments;
  }

  return removeEmptyPreferenceMaps(nextPreferences);
}

function applyItemVisibilityPreference(
  preferences: SideNavItemPreferenceMap,
  item: CustomizableNavItem,
  visibility: SideNavVisibility,
) {
  if (visibility === item.defaultVisibility) {
    delete preferences[item.id];
  } else {
    preferences[item.id] = { visibility };
  }
}

function applyObjectTypeVisibilityPreference(
  environments: Record<string, SideNavEnvironmentPreferencesWithPassthrough>,
  environmentId: string,
  objectTypeId: string,
  item: CustomizableNavItem,
  visibility: SideNavVisibility,
) {
  const nextEnvironmentPreferences: SideNavEnvironmentPreferencesWithPassthrough = {
    ...(environments[environmentId] ?? {}),
  };
  const nextObjectTypes = { ...(nextEnvironmentPreferences.object_types ?? {}) };

  if (visibility === item.defaultVisibility) {
    delete nextObjectTypes[objectTypeId];
  } else {
    nextObjectTypes[objectTypeId] = { visibility };
  }

  if (Object.keys(nextObjectTypes).length === 0) {
    delete nextEnvironmentPreferences.object_types;
  } else {
    nextEnvironmentPreferences.object_types = nextObjectTypes;
  }

  environments[environmentId] = nextEnvironmentPreferences;
}

function getEffectiveItemVisibility(
  item: NavItem,
  preferences: SideNavPreferences | undefined,
  environmentId?: string,
): SideNavVisibility {
  if (!isCustomizableNavItem(item)) {
    return 'show';
  }

  return getExplicitItemVisibility(item.id, preferences, environmentId) ?? getDefaultItemVisibility(item);
}

function isCustomizableNavItem(item: NavItem): boolean {
  return item.isCustomizable !== false && item.isMoreMenuOnly !== true;
}

function getCustomizableNavItemGroups(
  navItemGroups: NavItemGroup[],
  preferences: SideNavPreferences | undefined,
  environmentId?: string,
): CustomizableNavItemGroup[] {
  return navItemGroups
    .filter((group): group is NavItemGroup & { title: string } => group.title != null)
    .map((group) => ({
      title: group.title,
      items: group.items.filter(isCustomizableNavItem).map((item) => {
        const defaultVisibility = getDefaultItemVisibility(item);
        return {
          ...item,
          defaultVisibility,
          effectiveVisibility: getExplicitItemVisibility(item.id, preferences, environmentId) ?? defaultVisibility,
        };
      }),
    }))
    .filter((group) => group.items.length > 0);
}

export function getPresentationNavItemGroups(
  navItemGroups: NavItemGroup[],
  preferences: SideNavPreferences | undefined,
  currentUrl: string,
  environmentId?: string,
): PresentationNavItemGroup[] {
  return navItemGroups
    .map((group) => {
      if (group.title == null) {
        const items = group.items.filter((item) => item.isMoreMenuOnly !== true);

        return {
          ...group,
          isVisibleInSection: items.length > 0,
          items: items.map((item) => ({
            ...item,
            isVisibleWhenDeselected: true,
            isVisibleWhenSelected: true,
          })),
        };
      }

      const items = group.items
        .filter((item) => item.isMoreMenuOnly !== true)
        .map((item) => {
          const isVisibleWhenDeselected = getEffectiveItemVisibility(item, preferences, environmentId) === 'show';

          return {
            ...item,
            isVisibleWhenDeselected,
            isVisibleWhenSelected: true,
          };
        });

      return {
        ...group,
        isVisibleInSection: items.some(
          (item) => item.isVisibleWhenDeselected || (item.isVisibleWhenSelected && isNavItemActive(item, currentUrl)),
        ),
        items,
      };
    })
    .filter((group) => group.items.length > 0);
}

function getHiddenNavItems(
  navItemGroups: NavItemGroup[],
  preferences: SideNavPreferences | undefined,
  environmentId?: string,
): HiddenNavItem[] {
  return navItemGroups.flatMap((group) => {
    if (group.title == null) {
      return [];
    }
    const section = group.title;

    return group.items.flatMap((item) => {
      if (!isCustomizableNavItem(item) || getEffectiveItemVisibility(item, preferences, environmentId) === 'show') {
        return [];
      }

      return getMenuEntriesForHiddenItem(item, section);
    });
  });
}

function getPermanentMoreItems(navItemGroups: NavItemGroup[]): HiddenNavItem[] {
  return navItemGroups.flatMap((group) => {
    return group.items.flatMap((item) => {
      if (item.isMoreMenuOnly !== true) {
        return [];
      }

      return getMenuEntriesForHiddenItem(item, group.title ?? null);
    });
  });
}

function getPermanentMorePresentationItems(navItemGroups: NavItemGroup[]): PresentationNavItem[] {
  return getPermanentMoreItems(navItemGroups).map(({ item }) => ({
    ...item,
    isVisibleWhenDeselected: false,
    isVisibleWhenSelected: true,
  }));
}

function getMenuEntriesForHiddenItem(item: NavItem, section: string | null): HiddenNavItem[] {
  if (item.children == null || item.href != null) {
    return [{ item, section, parentItem: null }];
  }

  return item.children.map((child) => ({ item: child, section, parentItem: item.label }));
}

function getDefaultItemVisibility(item: NavItem): SideNavVisibility {
  return item.isHiddenByDefault === true ? 'hide' : 'show';
}

function getExplicitItemVisibility(
  itemId: string,
  preferences: SideNavPreferences | undefined,
  environmentId?: string,
): SideNavVisibility | undefined {
  const objectTypeId = getObjectTypePreferenceId(itemId);
  const visibility =
    objectTypeId != null && environmentId != null
      ? preferences?.environments?.[environmentId]?.object_types?.[objectTypeId]?.visibility
      : preferences?.items?.[itemId]?.visibility;

  if (visibility === 'show' || visibility === 'hide') {
    return visibility;
  }

  return undefined;
}

function getObjectTypePreferenceId(itemId: string): string | undefined {
  return itemId.startsWith(objectTypeNavItemIdPrefix) ? itemId.slice(objectTypeNavItemIdPrefix.length) : undefined;
}

function cloneSideNavPreferences(preferences: SideNavPreferences | undefined): SideNavPreferences {
  if (preferences == null) {
    return {};
  }

  return {
    ...preferences,
    ...(preferences.items != null ? { items: { ...preferences.items } } : {}),
    ...(preferences.environments != null
      ? {
          environments: Object.fromEntries(
            Object.entries(preferences.environments).map(([environmentId, environmentPreferences]) => [
              environmentId,
              {
                ...environmentPreferences,
                ...(environmentPreferences.items != null ? { items: { ...environmentPreferences.items } } : {}),
                ...(environmentPreferences.object_types != null
                  ? { object_types: { ...environmentPreferences.object_types } }
                  : {}),
              },
            ]),
          ),
        }
      : {}),
  };
}

function removeEmptyPreferenceMaps(preferences: SideNavPreferences): SideNavPreferences {
  const nextPreferences: SideNavPreferencesWithPassthrough = { ...preferences };

  if (nextPreferences.items != null && Object.keys(nextPreferences.items).length === 0) {
    delete nextPreferences.items;
  }

  if (nextPreferences.environments != null) {
    const environments = Object.fromEntries(
      Object.entries(nextPreferences.environments)
        .map(([environmentId, environmentPreferences]) => {
          const nextEnvironmentPreferences: SideNavEnvironmentPreferencesWithPassthrough = {
            ...environmentPreferences,
          };

          if (nextEnvironmentPreferences.items != null && Object.keys(nextEnvironmentPreferences.items).length === 0) {
            delete nextEnvironmentPreferences.items;
          }

          if (
            nextEnvironmentPreferences.object_types != null &&
            Object.keys(nextEnvironmentPreferences.object_types).length === 0
          ) {
            delete nextEnvironmentPreferences.object_types;
          }

          return [environmentId, nextEnvironmentPreferences] as const;
        })
        .filter(([, environmentPreferences]) => Object.keys(environmentPreferences).length > 0),
    );

    if (Object.keys(environments).length === 0) {
      delete nextPreferences.environments;
    } else {
      nextPreferences.environments = environments;
    }
  }

  return nextPreferences;
}
